"use client";
import { useAnalytics } from "@areahub/adapters";
import colors from "@areahub/theme/src/foundations/colors";
import {
  Box,
  Button as ChakraButton,
  Flex,
  Heading,
  Text,
} from "@chakra-ui/react";
import { Button, Button_T } from "@ui/components/button";
import { ImageFragment } from "lib/gcms";
import { useRouter } from "next/navigation";

export interface IndustriesSpecificHeroProps {
  title: string;
  /**
   * Text above the title
   */
  subtitle: string;
  /**
   * CTA buttons
   */
  buttons: Button_T[];
  /**
   * Description text below the title
   */
  pushText: string;
  image: ImageFragment;
  bg: string;
  titleColor: string;
  subtitleColor: string;
}

export const IndustriesSpecificHero = (
  props: IndustriesSpecificHeroProps & { slug: string },
) => {
  const analytics = useAnalytics();
  const router = useRouter();

  return (
    <Box
      backgroundImage={`url(${props?.image?.url})`}
      backgroundSize={"cover"}
      zIndex={0}
    >
      <Box
        position="absolute"
        top={0}
        left={0}
        width="100%"
        height="100%"
        backgroundColor="rgba(0, 0, 0, 0.65)"
        zIndex={0}
      />
      <Box
        position="absolute"
        top="0"
        left="0"
        width="full"
        height="full"
        bg="rgba(0, 0, 0, 0.3)"
        zIndex={1}
      />
      <Flex
        flexDir={{
          base: "column",
          lg: "row",
        }}
        pt={{
          base: 16,
          lg: 24,
        }}
        pl={{
          base: 5,
          md: 10,
          xl: 0,
        }}
        pb={{
          base: 12,
          lg: 20,
          xl: 28,
        }}
        pr={{
          base: 5,
          md: 10,
          lg: 0,
        }}
        overflow="visible"
        position="relative"
        maxW="1215px"
        mx="auto"
        zIndex={2}
      >
        <Flex
          flexDir="column"
          w={{
            lg: "80%",
          }}
        >
          <Flex flexDir={"row"} alignItems="center">
            <Heading
              as={"h2"}
              fontSize={{
                base: 16,
                md: 18,
                lg: 20,
              }}
              fontWeight="bold"
              color={props.subtitleColor}
              mb={{
                base: 4,
                lg: 6,
              }}
              mr={1}
              position="relative"
              lineHeight={1.3}
            >
              {props.subtitle}
            </Heading>
            <Box
              bgColor={props.subtitleColor}
              flex="1"
              h={{
                base: "2px",
                lg: "3px",
              }}
              mb={{
                base: 4,
                lg: 6,
              }}
            />
          </Flex>
          <Heading
            as={"h1"}
            fontSize={{
              base: 26,
              md: 34,
              lg: 38,
              xl: 45,
            }}
            textColor={props.titleColor}
            fontWeight={"bold"}
            lineHeight={1.2}
            mb={props.pushText ? 5 : 10}
          >
            {props.title}
          </Heading>
          {props.pushText && (
            <Text
              mb={12}
              fontSize={{
                base: 16,
                md: 18,
                lg: 19,
                xl: 20,
              }}
              fontWeight={"medium"}
              textColor={props.titleColor}
            >
              {props.pushText}
            </Text>
          )}
          <Flex
            flexDir={{
              base: "column",
              lg: "row",
            }}
            alignItems={{
              base: "center",
              lg: "unset",
            }}
            gap={{
              base: 4,
              lg: 5,
            }}
          >
            {props.buttons.map((btn, idx) => (
              <Button
                key={`hero-btn-${idx}`}
                bgColor={
                  idx === 0
                    ? colors.enterpriseColors.blue["200"]
                    : colors.enterpriseColors.blue["500"]
                }
                color={"white"}
                fontSize={{
                  base: 12,
                  md: 14,
                  lg: 16,
                  xl: 18,
                }}
                fontWeight={"bold"}
                py={{
                  base: 4,
                  lg: 7,
                  xl: 8,
                }}
                w={{
                  base: "140px",
                  md: "160px",
                  lg: "180px",
                  xl: "220px",
                }}
                onClick={() =>
                  idx === 0 &&
                  analytics.track(
                    `User clicked 'Request a Demo' button on ${props.slug} landing page`,
                    {
                      ui: "IndustriesSpecificHero",
                    },
                  )
                }
                textTransform={"uppercase"}
                {...btn}
              />
            ))}
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
};
